import { pricingPlansModalMessages } from '@/features/settings/billing/components/pricing-plans-modal/pricing-plans-modal.messages';
import { cn } from '@/lib/cn';
import {
  SegmentedControl,
  SegmentedControlList,
  SegmentedControlTrigger,
} from '@heidi/design-system/segmented-control';
import { FormattedMessage } from 'react-intl';

export type PlanSeat = 'standard' | 'max';

// Same compact overrides as `PlanPeriodTabs`: the DS ships one 36px neutral
// control, and the card's tab row is 24px. `h-6` keeps the orientation
// modifier so it outranks the DS `h-9` rule.
const LIST_CLASSES = 'p-0.5 group-data-[orientation=horizontal]/segmented-control:h-6';
const TRIGGER_CLASSES = 'h-5 gap-1 px-2 text-xs';
const PRACTICE_TIER_TRIGGER_CLASSES =
  'text-tier-practice-text hover:text-tier-practice-text data-[state=active]:text-tier-practice-text';

/**
 * Standard / Max seat toggle on the Practice column of the pricing plans modal
 * (DEV-13185: Practice Max is a seat within the Practice column, not a fourth
 * card). Sits in the row the period tabs occupy on the Clinician cards, since
 * Practice is annual-only.
 */
export function PlanSeatTabs({
  value,
  onValueChange,
  isHighlighted = false,
  className,
}: {
  value: PlanSeat;
  onValueChange: (seat: PlanSeat) => void;
  /** Practice-toned control when the column carries the highlight. */
  isHighlighted?: boolean;
  className?: string;
}) {
  return (
    <SegmentedControl
      className={cn('ms-auto', className)}
      value={value}
      onValueChange={(next) => {
        if (next === 'standard' || next === 'max') {
          onValueChange(next);
        }
      }}
      data-testid="practice-seat-tabs"
    >
      <SegmentedControlList
        className={cn(LIST_CLASSES, isHighlighted && 'bg-tier-practice-muted')}
      >
        <SegmentedControlTrigger
          value="standard"
          className={cn(TRIGGER_CLASSES, isHighlighted && PRACTICE_TIER_TRIGGER_CLASSES)}
        >
          <FormattedMessage {...pricingPlansModalMessages.seatStandard} />
        </SegmentedControlTrigger>
        <SegmentedControlTrigger
          value="max"
          className={cn(TRIGGER_CLASSES, isHighlighted && PRACTICE_TIER_TRIGGER_CLASSES)}
        >
          <FormattedMessage {...pricingPlansModalMessages.seatMax} />
        </SegmentedControlTrigger>
      </SegmentedControlList>
    </SegmentedControl>
  );
}
